import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';

const API_BASE = import.meta.env.VITE_API_URL || '';
const SW_PATH = '/sw.js';

type PushState = 'unsupported' | 'denied' | 'prompt' | 'subscribed' | 'unsubscribed' | 'loading';

export function usePushNotifications() {
  const { user, accessToken } = useAuth();
  const [state, setState] = useState<PushState>('loading');
  const subscriptionRef = useRef<PushSubscription | null>(null);

  // Check current state on mount
  useEffect(() => {
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
      setState('unsupported');
      return;
    }

    if (Notification.permission === 'denied') {
      setState('denied');
      return;
    }

    // Register SW and check existing subscription
    navigator.serviceWorker.register(SW_PATH).then((reg) => {
      return reg.pushManager.getSubscription();
    }).then((sub) => {
      subscriptionRef.current = sub;
      setState(sub ? 'subscribed' : 'unsubscribed');
    }).catch(() => {
      setState('unsubscribed');
    });
  }, []);

  const subscribe = useCallback(async () => {
    if (!accessToken || !user) return;

    try {
      // Get VAPID key from server
      const keyRes = await fetch(`${API_BASE}/api/push/vapid-key`);
      const { publicKey } = await keyRes.json();
      if (!publicKey) return;

      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey) as BufferSource,
      });

      subscriptionRef.current = sub;

      // Send subscription to server
      const subJson = sub.toJSON();
      await fetch(`${API_BASE}/api/push/subscribe`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          endpoint: subJson.endpoint,
          keys: subJson.keys,
        }),
      });

      setState('subscribed');
    } catch {
      // Permission denied or other error
      if (Notification.permission === 'denied') {
        setState('denied');
      }
    }
  }, [accessToken, user]);

  const unsubscribe = useCallback(async () => {
    if (!accessToken) return;

    const sub = subscriptionRef.current;
    if (!sub) return;

    try {
      await fetch(`${API_BASE}/api/push/subscribe`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({ endpoint: sub.endpoint }),
      });

      await sub.unsubscribe();
      subscriptionRef.current = null;
      setState('unsubscribed');
    } catch {
      // Ignore
    }
  }, [accessToken]);

  return { state, subscribe, unsubscribe };
}

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}
